<template>
  <div class="ap" :class="{ 'ap--page': page }" :style="themeVars">
    <!-- hero -->
    <div class="ap__hero">
      <button v-if="!page" type="button" class="ap__close" :aria-label="$t('close')" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </button>
      <div class="ap__avatar">
        <v-skeleton-loader v-if="!user" type="avatar" class="ap__avatar-skel" />
        <img v-else-if="user.avatar && user.avatar.link" :src="user.avatar.link" alt="">
        <span v-else class="ap__initials">{{ initials }}</span>
      </div>
    </div>

    <div class="ap__ident">
      <template v-if="user">
        <div class="ap__name">{{ user.name || '-' }}</div>
        <span v-if="user.role" class="ap__role">
          <v-icon x-small>mdi-shield-account-outline</v-icon>
          {{ user.role.name }}
        </span>
      </template>
      <template v-else>
        <v-skeleton-loader type="text" width="140" class="mx-auto" />
        <v-skeleton-loader type="chip" class="mx-auto mt-2" />
      </template>
    </div>

    <!-- details -->
    <div class="ap__section">
      <div class="ap__section-title">{{ $t('accountProfileInfo') }}</div>
      <div class="ap__list">
        <div v-for="row in rows" :key="row.key" class="ap__row">
          <span class="ap__row-icon"><v-icon small>{{ row.icon }}</v-icon></span>
          <div class="ap__row-text">
            <span class="ap__row-label">{{ $t(row.label) }}</span>
            <span v-if="user" class="ap__row-value" :dir="row.ltr ? 'ltr' : null">{{ row.value || '—' }}</span>
            <v-skeleton-loader v-else type="text" width="120" />
          </div>
          <button
            v-if="user && row.copy && row.value"
            type="button"
            class="ap__row-btn"
            :title="$t('copy')"
            @click="copy(row)"
          >
            <v-icon small>{{ copied === row.key ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
          </button>
          <span v-if="row.locked" class="ap__row-lock" :title="$t('accountProfilePasswordHint')">
            <v-icon small>mdi-lock-outline</v-icon>
          </span>
        </div>
      </div>
    </div>

    <div v-if="!page" class="ap__foot">
      <button type="button" class="ap__done" @click="$emit('close')">
        {{ $t('close') }}
      </button>
    </div>
  </div>
</template>

<script>
// Redesigned account profile for the app-bar "account settings" dialog.
// Same data call as pages/General/profile.vue (left untouched, it is also a
// route page): getdetail?select=getProfile&id=<userId>. Parent calls
// getProfile() after opening, like before.
import General from '@/Mixins/general'

function toHex (c) {
  if (!c) return null
  if (typeof c === 'object') c = c.base
  return /^#([0-9a-f]{6})$/i.test(c) ? c : null
}
// Blend hex colour `a` toward `b` by ratio t (0..1).
function mix (a, b, t) {
  const p = h => [1, 3, 5].map(i => parseInt(h.substr(i, 2), 16))
  const x = p(a)
  const y = p(b)
  return '#' + x.map((v, i) => Math.round(v + (y[i] - v) * t).toString(16).padStart(2, '0')).join('')
}

export default {
  name: 'AccountProfile',
  mixins: [General],
  props: {
    // true when shown inside a page that already has its own header (mobile)
    page: { type: Boolean, default: false },
  },
  data: () => ({
    user: null,
    loading: false,
    copied: null,
  }),
  computed: {
    // Hero and accents follow the Vuetify theme (primary) instead of a
    // hard-coded blue.
    themeVars () {
      const t = this.$vuetify.theme.currentTheme || {}
      const primary = toHex(t.primary) || '#0066ff'
      return {
        '--ap-primary': primary,
        '--ap-primary-2': mix(primary, '#ffffff', 0.25),
        '--ap-primary-3': mix(primary, '#ffffff', 0.45),
        '--ap-primary-soft': mix(primary, '#ffffff', 0.9),
        '--ap-primary-dark': mix(primary, '#000000', 0.2),
      }
    },
    initials () {
      const n = (this.user && this.user.name) || ''
      const parts = n.trim().split(/\s+/)
      return ((parts[0] || '').charAt(0) + (parts[1] || '').charAt(0)) || '?'
    },
    rows () {
      const u = this.user || {}
      return [
        { key: 'username', icon: 'mdi-account-outline', label: 'username', value: u.username, copy: true, ltr: true },
        { key: 'mobile', icon: 'mdi-cellphone', label: 'mobile', value: u.mobile, copy: true, ltr: true },
        { key: 'email', icon: 'mdi-email-outline', label: 'email', value: u.email, copy: true, ltr: true },
        { key: 'password', icon: 'mdi-key-outline', label: 'password', value: '••••••••••', locked: true },
      ]
    },
  },
  methods: {
    getProfile () {
      const app = this
      app.loading = true
      app.$axios.get(this.$endpoint + 'getdetail', {
        params: {
          select: 'getProfile',
          id: app.$store.state.userId,
        },
      })
        .then(res => {
          app.user = res.data
        })
        .catch(err => {
          app.serverErrorHandeler(err)
        })
        .finally(() => (app.loading = false))
    },
    copy (row) {
      if (!navigator.clipboard) return
      navigator.clipboard.writeText(String(row.value)).then(() => {
        this.copied = row.key
        clearTimeout(this.copyTimer)
        this.copyTimer = setTimeout(() => { this.copied = null }, 1500)
      })
    },
  },
}
</script>

<style scoped>
.ap {
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
}
.theme--dark .ap {
  background: #1e1e1e;
}
.ap--page {
  border-radius: 0;
}
.ap__hero {
  position: relative;
  height: 112px;
  background:
    radial-gradient(circle at 15% 20%, rgba(255, 255, 255, 0.18) 0, transparent 45%),
    linear-gradient(135deg, var(--ap-primary) 0%, var(--ap-primary-2) 55%, var(--ap-primary-3) 100%);
}
.ap__close {
  position: absolute;
  top: 10px;
  inset-inline-end: 10px;
  width: 34px;
  height: 34px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.18);
  transition: background 0.15s;
}
.ap__close:hover {
  background: rgba(255, 255, 255, 0.3);
}
.ap__close .v-icon {
  color: #fff !important;
}
.ap__avatar {
  position: absolute;
  left: 50%;
  bottom: -46px;
  transform: translateX(-50%);
  width: 96px;
  height: 96px;
  border-radius: 50%;
  border: 4px solid #fff;
  background: var(--ap-primary-soft);
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(16, 24, 40, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}
.theme--dark .ap__avatar {
  border-color: #1e1e1e;
  background: #2a2a2a;
}
.ap__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ap__avatar-skel ::v-deep .v-skeleton-loader__avatar {
  width: 88px;
  height: 88px;
}
.ap__initials {
  font-size: 1.9rem;
  font-weight: 700;
  color: var(--ap-primary);
}
.ap__ident {
  padding: 56px 20px 4px;
  text-align: center;
}
.ap__name {
  font-size: 1.15rem;
  font-weight: 700;
}
.ap__role {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  height: 24px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  color: var(--ap-primary-dark);
  background: var(--ap-primary-soft);
}
.ap__role .v-icon {
  color: inherit;
}
.theme--dark .ap__role {
  color: #9cc2ff;
  background: rgba(61, 139, 255, 0.16);
}
.ap__section {
  padding: 16px 20px 8px;
}
.ap__section-title {
  font-size: 0.74rem;
  font-weight: 700;
  color: #8a94a6;
  margin-bottom: 8px;
}
.ap__list {
  border: 1px solid #e8ebf0;
  border-radius: 6px;
  overflow: hidden;
}
.theme--dark .ap__list {
  border-color: #333;
}
.ap__row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 58px;
  padding: 8px 12px;
}
.ap__row + .ap__row {
  border-top: 1px solid #f0f2f5;
}
.theme--dark .ap__row + .ap__row {
  border-color: #2a2a2a;
}
.ap__row-icon {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: #f1f4f9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.theme--dark .ap__row-icon {
  background: #2a2a2a;
}
.ap__row-icon .v-icon {
  color: #5b6475;
}
.ap__row-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.ap__row-label {
  font-size: 0.72rem;
  color: #8a94a6;
}
.ap__row-value {
  font-size: 0.9rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: start;
}
/* LTR values (numbers, email) still line up with their RTL labels */
.v-application--is-rtl .ap__row-value[dir='ltr'] {
  text-align: right;
}
.ap__row-btn,
.ap__row-lock {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ap__row-btn:hover {
  background: #eef1f6;
}
.ap__row-btn .v-icon {
  color: var(--ap-primary) !important;
}
.ap__row-lock .v-icon {
  color: #b3bac6;
}
.ap__foot {
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px 18px;
}
.ap__done {
  height: 38px;
  min-width: 96px;
  padding: 0 18px;
  border-radius: 6px;
  border: 1px solid #dfe3ea;
  background: #fff;
  color: #374151;
  font-size: 0.88rem;
  transition: border-color 0.15s, background 0.15s;
}
.ap__done:hover {
  border-color: #b8c0cc;
  background: #f8f9fb;
}
.theme--dark .ap__done {
  background: #1e1e1e;
  border-color: #3a3a3a;
  color: #ddd;
}
</style>
